import React from "react";
import { Grid, Skeleton, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import {
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";

import { formatFinance } from "../../utils/formatters";
import LabeledIconCard from "../ui/cards/LabeledIconCard";

// Animation variants
const cardVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: (custom) => ({
    y: 0,
    opacity: 1,
    transition: { delay: custom * 0.1, duration: 0.5 },
  }),
};

/**
 * Component displaying summary statistics in cards
 * @param {Object} props - Component props
 * @param {number} props.totalCosts - Total costs value
 * @param {number} props.categoriesCount - Number of categories
 * @param {number} props.suppliersCount - Number of suppliers
 * @param {number} props.averageCost - Average cost value
 * @param {boolean} props.loading - Loading state
 * @returns {JSX.Element} Summary cards component
 */
const CostsSummaryCards = ({
  totalCosts,
  categoriesCount,
  suppliersCount,
  averageCost,
  loading,
}) => {
  const theme = useTheme();

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <motion.div
          custom={0}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
        >
          <LabeledIconCard
            icon={<MoneyIcon fontSize="small" />}
            title="Custos Totais"
            value={
              loading ? (
                <Skeleton width="100%" />
              ) : (
                formatFinance(totalCosts / 100)
              )
            }
            color={theme.palette.primary.main}
          />
        </motion.div>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <motion.div
          custom={1}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
        >
          <LabeledIconCard
            icon={<CategoryIcon fontSize="small" />}
            title="Categorias de Custo"
            value={loading ? <Skeleton width="100%" /> : categoriesCount}
            color={theme.palette.secondary.main}
          />
        </motion.div>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <motion.div
          custom={2}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
        >
          <LabeledIconCard
            icon={<BusinessIcon fontSize="small" />}
            title="Fornecedores Ativos"
            value={loading ? <Skeleton width="100%" /> : suppliersCount}
            color={theme.palette.success.main}
          />
        </motion.div>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
        >
          <LabeledIconCard
            icon={<TrendingUpIcon fontSize="small" />}
            title="Custo Médio"
            value={
              loading ? (
                <Skeleton width="100%" />
              ) : (
                formatFinance(averageCost / 100)
              )
            }
            color={theme.palette.info.main}
          />
        </motion.div>
      </Grid>
    </Grid>
  );
};

export default CostsSummaryCards;
